<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { apiGet, apiPost, apiPut, apiSend } from "../api";
import { useToast } from "../stores/toast";

const toast = useToast();

interface Category { slug: string; name: string }
interface Variant { size?: string; color?: string; colorSwatch?: string; stock: number }
interface ProductImage { id: string; url: string; srcset: string; width: number; height: number; alt?: string }
interface ProductDetail {
  id: string; sku: string; name: string; description?: string; category: string;
  price: { amount: number }; vatRate?: number; status: "live" | "concept"; stockTotal: number;
  variants: Variant[]; images?: ProductImage[];
}

const route = useRoute();
const router = useRouter();
const id = computed(() => route.params.id as string | undefined);
const isEdit = computed(() => !!id.value);

const name = ref("");
const description = ref("");
const category = ref("slippers");
const priceEuro = ref("0,00");
const vatRate = ref(21);
const status = ref<"live" | "concept">("concept");
const sku = ref("");
const stockTotalInput = ref(0);

const colors = ref<{ name: string; swatch: string }[]>([]);
const sizes = ref<string[]>([]);
const stock = ref<Record<string, string>>({}); // key `${color}|${size}` → stock (leeg = n.v.t.)

const categories = ref<Category[]>([]);
const saving = ref(false);
const error = ref("");

// --- Foto's ---
const images = ref<ProductImage[]>([]);
const uploading = ref(0);
const uploadErr = ref("");
const dragOver = ref(false);
const fileInput = ref<HTMLInputElement | null>(null);

async function handleFiles(files: FileList | File[]) {
  if (!id.value) return;
  uploadErr.value = "";
  for (const file of Array.from(files)) {
    if (!file.type.startsWith("image/")) { uploadErr.value = "Alleen afbeeldingen (jpg, png, webp)"; continue; }
    uploading.value++;
    try {
      // 1) presigned URL aanvragen → 2) origineel direct naar S3 → 3) laten verwerken.
      const { imageId, uploadUrl } = await apiPost<{ imageId: string; uploadUrl: string }>(
        `/products/${id.value}/images`, { contentType: file.type },
      );
      const put = await fetch(uploadUrl, { method: "PUT", headers: { "content-type": file.type }, body: file });
      if (!put.ok) throw new Error(`S3-upload mislukt (${put.status})`);
      const img = await apiPut<ProductImage>(`/products/${id.value}/images/${imageId}`, {});
      images.value.push(img);
    } catch (e) {
      uploadErr.value = `Upload mislukt: ${(e as Error).message}`;
    } finally {
      uploading.value--;
    }
  }
}
function onPick(e: Event) {
  const t = e.target as HTMLInputElement;
  if (t.files?.length) handleFiles(t.files);
  t.value = "";
}
function onDrop(e: DragEvent) {
  dragOver.value = false;
  if (e.dataTransfer?.files?.length) handleFiles(e.dataTransfer.files);
}
async function deleteImage(img: ProductImage) {
  if (!id.value) return;
  try {
    await apiSend("DELETE", `/products/${id.value}/images/${img.id}`);
    images.value = images.value.filter((i) => i.id !== img.id);
    toast.show("Foto verwijderd");
  } catch (e) { uploadErr.value = `Verwijderen mislukt: ${(e as Error).message}`; }
}

const deleting = ref(false);
async function removeProduct() {
  if (!id.value) return;
  if (!confirm(`Product "${name.value}" verwijderen?`)) return;
  deleting.value = true;
  try {
    await apiSend("DELETE", `/products/${id.value}`);
    toast.show("Product verwijderd");
    router.push("/producten");
  } catch (e) { error.value = `Verwijderen mislukt: ${(e as Error).message}`; } finally { deleting.value = false; }
}

const key = (c: string, s: string) => `${c}|${s}`;
const newColorName = ref("");
const newColorSwatch = ref("#f2a9b0");
const newSize = ref("");

function addColor() {
  if (!newColorName.value.trim()) return;
  colors.value.push({ name: newColorName.value.trim(), swatch: newColorSwatch.value });
  newColorName.value = "";
}
function removeColor(i: number) { colors.value.splice(i, 1); }
function addSize() {
  const s = newSize.value.trim();
  if (s && !sizes.value.includes(s)) sizes.value.push(s);
  newSize.value = "";
}
function removeSize(i: number) { sizes.value.splice(i, 1); }

const hasMatrix = computed(() => colors.value.length > 0 && sizes.value.length > 0);
const rowTotal = (c: string) =>
  sizes.value.reduce((sum, s) => sum + (Number(stock.value[key(c, s)]) || 0), 0);

const euroToCents = (v: string) => Math.round(parseFloat(v.replace(/\./g, "").replace(",", ".")) * 100) || 0;
const centsToEuro = (c: number) => (c / 100).toFixed(2).replace(".", ",");

onMounted(async () => {
  try {
    const cats = await apiGet<{ items: Category[] }>("/categories");
    categories.value = cats.items;
  } catch { /* categorieën optioneel voor het formulier */ }

  if (isEdit.value && id.value) {
    try {
      const p = await apiGet<ProductDetail>(`/products/${id.value}`);
      name.value = p.name;
      description.value = p.description ?? "";
      category.value = p.category;
      priceEuro.value = centsToEuro(p.price.amount);
      vatRate.value = p.vatRate ?? 21;
      status.value = p.status;
      sku.value = p.sku;
      stockTotalInput.value = p.stockTotal;
      // Varianten → kleuren/maten/stock-map reconstrueren.
      const colorMap = new Map<string, string>();
      const sizeSet = new Set<string>();
      for (const v of p.variants) {
        if (v.color) colorMap.set(v.color, v.colorSwatch || "#cccccc");
        if (v.size) sizeSet.add(v.size);
        if (v.color && v.size) stock.value[key(v.color, v.size)] = String(v.stock);
      }
      colors.value = [...colorMap].map(([n, s]) => ({ name: n, swatch: s }));
      sizes.value = [...sizeSet];
      images.value = p.images ?? [];
    } catch (e) { error.value = `Laden mislukt: ${e}`; }
  }
});

async function save() {
  error.value = ""; saving.value = true;
  try {
    const variants: Variant[] = [];
    if (hasMatrix.value) {
      for (const c of colors.value) {
        for (const s of sizes.value) {
          const raw = stock.value[key(c.name, s)];
          if (raw !== undefined && raw !== "") {
            variants.push({ size: s, color: c.name, colorSwatch: c.swatch, stock: Number(raw) || 0 });
          }
        }
      }
    }
    const payload = {
      sku: sku.value || undefined,
      name: name.value,
      description: description.value || undefined,
      category: category.value,
      priceCents: euroToCents(priceEuro.value),
      vatRate: vatRate.value,
      status: status.value,
      stockTotal: hasMatrix.value ? undefined : stockTotalInput.value,
      variants: variants.length ? variants : undefined,
    };
    if (isEdit.value && id.value) {
      await apiPut(`/products/${id.value}`, payload);
      toast.show("Product bijgewerkt");
      router.push("/producten");
    } else {
      const { id: newId } = await apiPost<{ id: string }>("/products", payload);
      toast.show("Aangemaakt — voeg nu foto's toe");
      // Naar de editor zodat foto's toegevoegd kunnen worden (uploaden vereist een product-id).
      router.push(`/producten/${newId}/bewerken`);
    }
  } catch (e) {
    error.value = `Opslaan mislukt: ${(e as Error).message}`;
  } finally {
    saving.value = false;
  }
}
</script>

<template>
  <div class="editor">
    <header class="ehead">
      <RouterLink to="/producten" class="back">←</RouterLink>
      <h1 class="title">{{ isEdit ? name || "Product bewerken" : "Nieuw product" }}</h1>
      <div class="actions">
        <button v-if="isEdit" class="btn danger" :disabled="deleting" @click="removeProduct">Verwijderen</button>
        <RouterLink to="/producten" class="btn ghost">Annuleren</RouterLink>
        <button class="btn primary" :disabled="saving || !name" @click="save">{{ saving ? "Opslaan…" : "Product opslaan" }}</button>
      </div>
    </header>

    <p v-if="error" class="err">{{ error }}</p>

    <div class="grid">
      <!-- Links: foto's -->
      <section class="card">
        <h2 class="label">Productfoto's</h2>
        <div v-if="!isEdit" class="dropzone hint">Bewaar het product eerst — daarna kun je foto's toevoegen.</div>
        <template v-else>
          <div
            class="dropzone"
            :class="{ over: dragOver }"
            @click="fileInput?.click()"
            @dragover.prevent="dragOver = true"
            @dragleave.prevent="dragOver = false"
            @drop.prevent="onDrop"
          >
            <span v-if="uploading">{{ uploading }} foto('s) uploaden…</span>
            <span v-else>Sleep foto's hierheen of <u>kies bestanden</u><br /><small>jpg, png of webp · max 15 MB</small></span>
          </div>
          <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/webp" multiple hidden @change="onPick" />
          <p v-if="uploadErr" class="uploaderr">{{ uploadErr }}</p>
          <div class="thumbs">
            <div v-for="img in images" :key="img.id" class="thumb filled">
              <img :src="img.url" :alt="img.alt || name" />
              <button class="delimg" @click.stop="deleteImage(img)" aria-label="Foto verwijderen">×</button>
            </div>
          </div>
        </template>
      </section>

      <!-- Rechts: velden -->
      <section class="card">
        <label class="fl">Productnaam</label>
        <input v-model="name" class="inp" placeholder="bv. Cloud Slide — blush" />
        <label class="fl">Omschrijving</label>
        <textarea v-model="description" class="inp" rows="3" />
        <div class="row2">
          <div>
            <label class="fl">Categorie</label>
            <select v-model="category" class="inp">
              <option v-for="c in categories" :key="c.slug" :value="c.slug">{{ c.name }}</option>
            </select>
          </div>
          <div>
            <label class="fl">Prijs (€)</label>
            <input v-model="priceEuro" class="inp" placeholder="24,95" />
          </div>
        </div>
        <div class="row2">
          <div>
            <label class="fl">BTW (%)</label>
            <input v-model.number="vatRate" type="number" class="inp" />
          </div>
          <div>
            <label class="fl">SKU</label>
            <input v-model="sku" class="inp" placeholder="auto indien leeg" />
          </div>
        </div>
        <label class="fl">Status</label>
        <div class="seg">
          <button :class="{ on: status === 'live' }" @click="status = 'live'">Live</button>
          <button :class="{ on: status === 'concept' }" @click="status = 'concept'">Concept</button>
        </div>
      </section>
    </div>

    <!-- Variant-matrix -->
    <section class="card">
      <h2 class="label">Maten &amp; kleuren (voorraad)</h2>
      <div class="manage">
        <div class="mgroup">
          <span class="mtitle">Kleuren</span>
          <span v-for="(c, i) in colors" :key="i" class="chip">
            <span class="sw" :style="{ background: c.swatch }" /> {{ c.name }}
            <button class="x" @click="removeColor(i)">×</button>
          </span>
          <span class="addrow">
            <input v-model="newColorName" placeholder="kleur" class="mini" @keyup.enter="addColor" />
            <input v-model="newColorSwatch" type="color" class="colorpick" />
            <button class="addbtn" @click="addColor">+</button>
          </span>
        </div>
        <div class="mgroup">
          <span class="mtitle">Maten</span>
          <span v-for="(s, i) in sizes" :key="i" class="chip">{{ s }}<button class="x" @click="removeSize(i)">×</button></span>
          <span class="addrow">
            <input v-model="newSize" placeholder="maat" class="mini" @keyup.enter="addSize" />
            <button class="addbtn" @click="addSize">+</button>
          </span>
        </div>
      </div>

      <table v-if="hasMatrix" class="matrix">
        <thead>
          <tr><th>Kleur \ maat</th><th v-for="s in sizes" :key="s">{{ s }}</th><th>Totaal</th></tr>
        </thead>
        <tbody>
          <tr v-for="c in colors" :key="c.name">
            <td class="ch"><span class="sw" :style="{ background: c.swatch }" /> {{ c.name }}</td>
            <td v-for="s in sizes" :key="s"><input v-model="stock[key(c.name, s)]" class="cell" placeholder="—" /></td>
            <td class="rt">{{ rowTotal(c.name) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else class="nototal">
        <label class="fl">Totale voorraad (geen maat/kleur-varianten)</label>
        <input v-model.number="stockTotalInput" type="number" class="inp small" />
      </div>
    </section>
  </div>
</template>

<style scoped>
.editor { max-width: 1000px; }
.ehead { display: flex; align-items: center; gap: 14px; margin-bottom: 18px; }
.back { font-size: 22px; text-decoration: none; color: var(--ink); }
.title { font-family: var(--font-ui); font-weight: 700; font-size: 22px; margin: 0; }
.actions { margin-left: auto; display: flex; gap: 10px; }
.btn { border: 0; border-radius: 999px; padding: 10px 18px; font-weight: 700; font-size: 14px; cursor: pointer; text-decoration: none; }
.btn.ghost { background: var(--warm); color: var(--ink); }
.btn.primary { background: var(--coral); color: #fff; }
.btn:disabled { opacity: 0.6; cursor: not-allowed; }
.err { color: var(--red); background: var(--red-bg); padding: 10px 14px; border-radius: 10px; }
.grid { display: grid; grid-template-columns: 1.1fr 1fr; gap: 18px; margin-bottom: 18px; }
.card { background: var(--surface); border: 1px solid var(--hairline); border-radius: 14px; padding: 18px; }
.label { font-family: var(--font-ui); font-weight: 600; font-size: 15px; margin: 0 0 12px; }
.dropzone { aspect-ratio: 4/3; background: var(--img-placeholder); border-radius: 12px; display: grid; place-items: center; color: var(--muted); font-size: 13px; text-align: center; padding: 20px; cursor: pointer; }
.dropzone.over { outline: 2px dashed var(--coral); outline-offset: -6px; color: var(--ink); }
.dropzone.hint { cursor: default; }
.dropzone u { color: var(--coral); }
.dropzone small { color: var(--muted); }
.uploaderr { color: var(--red); font-size: 13px; margin: 8px 0 0; }
.thumbs { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 10px; }
.thumb { position: relative; width: 64px; height: 64px; border-radius: 8px; background: var(--img-placeholder); overflow: hidden; }
.thumb.filled img { width: 100%; height: 100%; object-fit: cover; display: block; }
.delimg { position: absolute; top: 3px; right: 3px; width: 20px; height: 20px; border-radius: 999px; border: 0; background: rgba(20,20,20,.78); color: #fff; cursor: pointer; font-size: 14px; line-height: 1; display: grid; place-items: center; }
.fl { display: block; font-size: 12px; font-weight: 600; color: var(--muted); text-transform: uppercase; letter-spacing: 0.04em; margin: 12px 0 6px; }
.inp { width: 100%; padding: 10px 12px; border: 1px solid var(--border-input); border-radius: 10px; background: var(--surface); font-size: 14px; font-family: inherit; }
.inp.small { max-width: 160px; }
.row2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.seg { display: inline-flex; border: 1px solid var(--border-input); border-radius: 999px; overflow: hidden; }
.seg button { border: 0; background: var(--surface); padding: 8px 18px; font-weight: 600; cursor: pointer; }
.seg button.on { background: var(--ink); color: #fff; }
.manage { display: flex; gap: 30px; flex-wrap: wrap; margin-bottom: 16px; }
.mgroup { display: flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.mtitle { font-size: 13px; color: var(--muted); font-weight: 600; }
.chip { display: inline-flex; align-items: center; gap: 6px; background: var(--warm); border-radius: 999px; padding: 5px 10px; font-size: 13px; font-weight: 600; }
.sw { width: 14px; height: 14px; border-radius: 999px; border: 1px solid rgba(0,0,0,.1); }
.x { border: 0; background: none; cursor: pointer; color: var(--muted); font-size: 14px; }
.addrow { display: inline-flex; align-items: center; gap: 6px; }
.mini { width: 80px; padding: 6px 8px; border: 1px solid var(--border-input); border-radius: 8px; font-size: 13px; }
.colorpick { width: 32px; height: 30px; border: 1px solid var(--border-input); border-radius: 8px; padding: 0; }
.addbtn { width: 28px; height: 28px; border-radius: 8px; border: 1px solid var(--border-input); background: var(--surface); cursor: pointer; }
.matrix { width: 100%; border-collapse: collapse; font-size: 14px; }
.matrix th { text-align: center; color: var(--muted); font-size: 12px; padding: 8px; border-bottom: 1px solid var(--hairline); }
.matrix th:first-child { text-align: left; }
.matrix td { padding: 8px; border-bottom: 1px solid var(--hairline); text-align: center; }
.ch { display: flex; align-items: center; gap: 8px; font-weight: 600; }
.cell { width: 60px; padding: 7px; border: 1px solid var(--border-input); border-radius: 8px; text-align: center; }
.rt { font-weight: 700; }
.nototal { margin-top: 6px; }
</style>
